// eslint-disable-next-line no-restricted-imports
import dayjs from 'dayjs';
// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isToday from 'dayjs/plugin/isToday';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

// Locale data for the languages the dashboard ships. Imported eagerly (about
// 4 KB gzipped for all four) because `dayjs.locale()` is a process-wide switch:
// the dashboard sets it from the active locale, so any date rendered anywhere -
// including `AtlasDateTime.relative` and plain `format('lll')` calls - comes out
// in the reader's language instead of English.
// eslint-disable-next-line no-restricted-imports
import 'dayjs/locale/de';
// eslint-disable-next-line no-restricted-imports
import 'dayjs/locale/es';
// eslint-disable-next-line no-restricted-imports
import 'dayjs/locale/fr';
// eslint-disable-next-line no-restricted-imports
import 'dayjs/locale/pt';

dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isToday);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekday);

/** Locales this module ships data for. Anything else leaves English in place. */
const SUPPORTED_LOCALES = new Set(['en', 'es', 'pt', 'fr', 'de']);

/**
 * Point dayjs at `locale`, so formatted and relative dates follow the reader.
 *
 * Called by the locale store whenever the active locale changes. Unknown or
 * region-tagged values (say `en-GB`) fall back to their language subtag, and
 * anything unrecognised leaves the current locale alone rather than throwing
 * mid-render.
 */
export function setDayjsLocale(locale: string): void {
    const language = locale.toLowerCase().split('-')[0];

    if (SUPPORTED_LOCALES.has(language)) dayjs.locale(language);
}

/**
 * The same instant, formatted with whatever locale is active *now*.
 *
 * A dayjs instance captures its locale when it is created, so a `Dayjs` held in
 * a model, a cache or a memo keeps formatting in the language that was active
 * back then. Anything that renders an existing instance goes through here so a
 * locale switch shows up in already-built dates too. Without it, switching to
 * German leaves every previously created date in English for its lifetime.
 */
export function withActiveLocale(date: Dayjs): Dayjs {
    return date.locale(dayjs.locale());
}

export default dayjs;

// #region Types

// eslint-disable-next-line no-restricted-imports
export type { Dayjs } from 'dayjs';

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect, afterEach } = import.meta.vitest;

    describe('dayjs', () => {
        afterEach(() => {
            setDayjsLocale('en');
        });

        it('formats dates in the configured language', () => {
            setDayjsLocale('de');
            const date = dayjs('2024-05-06T18:30:00');

            expect(date.format('ll')).toBe('6. Mai 2024');
            expect(date.format('LT')).toBe('18:30');

            setDayjsLocale('fr');
            expect(dayjs('2024-05-06T18:30:00').format('ll')).toBe('6 mai 2024');
        });

        it('re-locales an instance that was built under the previous locale', () => {
            setDayjsLocale('en');
            const date = dayjs('2024-05-06T18:30:00');
            expect(date.format('ll')).toBe('May 6, 2024');

            setDayjsLocale('de');
            expect(withActiveLocale(date).format('ll')).toBe('6. Mai 2024');
        });

        it('translates relative times, which is what the dashboard shows', () => {
            setDayjsLocale('es');
            const twoHoursAgo = dayjs().subtract(2, 'hour');
            expect(twoHoursAgo.fromNow()).toBe('hace 2 horas');

            setDayjsLocale('en');
            expect(withActiveLocale(twoHoursAgo).fromNow()).toBe('2 hours ago');
        });

        it('accepts a region tag and ignores what it has no data for', () => {
            setDayjsLocale('pt-BR');
            expect(dayjs.locale()).toBe('pt');

            setDayjsLocale('klingon');
            expect(dayjs.locale()).toBe('pt');
        });
    });
}

// #endregion
